import React from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import usePageMeta from '../hooks/usePageMeta';
import { trackEvent } from '../analytics';
import { OPERATIONS, MOTION_MODES } from '../lib/operationCopy';
import { displayNameOf } from '../lib/operations';
import './FeaturesPage.css';

// Features index page (/features). Landing for all three public-facing motion
// and quality operations: MotionPace, MotionTrim, and Deflicker.
//
// Like MotionPacePage, this route is gated at BUILD time (REACT_APP_MOTIONPACE_MARKETING).
// The component itself is unaware of the gate — it only has to render the same
// HTML for a crawler as for a signed-in human (no data-fetching, no flag reads here).

const PAGE_TITLE = 'Features — What Tweedia Does to Your Footage | Tweedia Studio';
const PAGE_DESCRIPTION =
  'MotionPace adapts timelapse speed to the action. MotionTrim cuts the still stretches out. ' +
  'Deflicker removes the exposure flicker that fixed cameras pick up. ' +
  'All three run in one pass — no software to install, no editing required.';
const CANONICAL_PATH = '/features';

// Canonical names from the naming source: operationCopy.js (#379) and operations.js (#400).
// The display name for deflicker reads as "Fix flicker" — import it, never retype it.
const FIX_FLICKER = displayNameOf('deflicker');
const MOTIONTRIM_NAME = MOTION_MODES.trim.name;
const MOTIONPACE_NAME = OPERATIONS.variable_speed.displayName;

const FEATURE_CARDS = [
  {
    id: 'motionpace',
    name: MOTIONPACE_NAME,
    to: '/motionpace',
    ctaLabel: 'Learn about MotionPace',
    desc:
      'The same per-frame motion measurement, used to vary the playback speed instead of cutting. ' +
      'Busy stretches play at 1× — the full shoot is still in there, nothing removed, just paced.',
    art: (
      // Speed-curve icon: line that rises and falls across a filmstrip baseline.
      <svg width="84" height="48" viewBox="0 0 84 48" fill="none" aria-hidden="true">
        <path d="M6 40h72" stroke="#2a2e38" strokeWidth="1.5" strokeLinecap="round" />
        {/* frame ticks */}
        <g stroke="#3a424f" strokeWidth="1" strokeLinecap="round">
          <line x1="14" y1="40" x2="14" y2="44" />
          <line x1="28" y1="40" x2="28" y2="44" />
          <line x1="42" y1="40" x2="42" y2="44" />
          <line x1="56" y1="40" x2="56" y2="44" />
          <line x1="70" y1="40" x2="70" y2="44" />
        </g>
        {/* speed curve: slow (high on chart) then fast (low) then back */}
        <path
          d="M6 14 C14 14 18 36 28 36 C38 36 42 14 56 14 C65 14 68 28 78 28"
          fill="none"
          stroke="#00b3a6"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    id: 'motiontrim',
    name: MOTIONTRIM_NAME,
    to: '/motiontrim',
    ctaLabel: 'Learn about MotionTrim',
    desc:
      'The same motion pass, but still stretches are cut rather than sped through. ' +
      'The result is shorter — only the frames where something was actually moving stay in.',
    art: (
      // Filmstrip with greyed-out still segments and scissors-cut gap.
      <svg width="84" height="48" viewBox="0 0 84 48" fill="none" aria-hidden="true">
        {/* active frames */}
        <rect x="6"  y="18" width="12" height="18" rx="2" stroke="#00b3a6" strokeWidth="1.4" strokeOpacity=".9" />
        <rect x="20" y="18" width="12" height="18" rx="2" stroke="#00b3a6" strokeWidth="1.4" strokeOpacity=".9" />
        {/* cut gap */}
        <line x1="35" y1="14" x2="35" y2="42" stroke="#3a424f" strokeWidth="1" strokeDasharray="3 2" />
        {/* still frames — greyed */}
        <rect x="37" y="18" width="10" height="18" rx="2" stroke="#3a424f" strokeWidth="1.2" strokeOpacity=".5" />
        <rect x="49" y="18" width="10" height="18" rx="2" stroke="#3a424f" strokeWidth="1.2" strokeOpacity=".5" />
        {/* cut marker */}
        <path d="M33 13l4 4M33 17l4-4" stroke="#00b3a6" strokeWidth="1.4" strokeLinecap="round" />
        {/* active frames after cut */}
        <rect x="62" y="18" width="12" height="18" rx="2" stroke="#00b3a6" strokeWidth="1.4" strokeOpacity=".9" />
      </svg>
    ),
  },
  {
    id: 'deflicker',
    name: FIX_FLICKER,
    to: '/deflicker-timelapse',
    ctaLabel: `Learn about ${FIX_FLICKER}`,
    desc:
      'Fixed cameras pick up per-frame exposure variation that reads as flicker at timelapse speed. ' +
      'One pass smooths the luminance track across every frame so the flicker disappears.',
    art: (
      // Waveform: jagged (before) then smoothed (after).
      <svg width="84" height="48" viewBox="0 0 84 48" fill="none" aria-hidden="true">
        {/* jagged raw track */}
        <path
          d="M6 28 L12 16 L18 34 L24 14 L30 30 L36 20"
          stroke="#5a6572"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* divider */}
        <line x1="40" y1="10" x2="40" y2="42" stroke="#2a2e38" strokeWidth="1" strokeDasharray="3 2" />
        {/* smooth track */}
        <path
          d="M44 24 C50 24 56 24 62 24 C68 24 72 24 78 24"
          stroke="#00b3a6"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
];

function FeaturesPage({ user, signIn }) {
  const signedIn = !!user;

  usePageMeta({
    title: PAGE_TITLE,
    description: PAGE_DESCRIPTION,
    canonicalPath: CANONICAL_PATH,
  });

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleCtaClick = (ctaId) => () => {
    trackEvent('cta_click', { cta_id: ctaId, signed_in: signedIn });
  };

  const handleSignInCta = (ctaId) => () => {
    trackEvent('cta_click', { cta_id: ctaId, signed_in: false });
    signIn();
  };

  const handleCardClick = (featureId) => () => {
    trackEvent('nav_click', { target: `features_card_${featureId}` });
  };

  return (
    <div className="features-page">
      <Header user={user} signIn={signIn} />
      <main className="ft-main" data-testid="features-main">

        {/* ---------------- hero ---------------- */}
        <section className="ft-hero" id="ft-hero" data-testid="features-hero">
          <div className="ft-hero-glow" aria-hidden="true" />
          <div className="ft-wrap">
            <span className="ft-badge">
              <i className="ft-badge-dot" aria-hidden="true" />
              Tweedia Studio
            </span>
            <h1>What Tweedia does to your footage.</h1>
            <p className="ft-hero-sub">
              One motion pass reads every frame. Two ways to use it: pace through the still
              stretches, or cut them out. A separate pass smooths the flicker fixed cameras
              always pick up. All automated — no editing required.
            </p>
            <div className="ft-motion-callout" data-testid="features-motion-callout">
              <p>
                <strong>One motion pass. Two ways to use it.</strong>{' '}
                {MOTIONPACE_NAME} and {MOTIONTRIM_NAME} share the same per-frame motion
                measurement — choose the output that fits your shoot.
              </p>
            </div>
          </div>
        </section>

        {/* ---------------- feature cards ---------------- */}
        <section className="ft-cards-section" data-testid="features-cards">
          <div className="ft-wrap">
            <p className="ft-section-label">What each feature does</p>
            <div className="ft-cards">
              {FEATURE_CARDS.map((card) => (
                <Link
                  key={card.id}
                  to={card.to}
                  className="ft-card"
                  data-testid={`features-card-${card.id}`}
                  onClick={handleCardClick(card.id)}
                >
                  <div className="ft-card-art">{card.art}</div>
                  <div className="ft-card-name">{card.name}</div>
                  <p className="ft-card-desc">{card.desc}</p>
                  <span className="ft-card-cta" aria-hidden="true">
                    {card.ctaLabel} →
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* ---------------- final CTA ---------------- */}
        <div className="ft-wrap">
          <section className="ft-final" id="ft-start" data-testid="features-final-cta">
            <h2>Ready to process your first shoot?</h2>
            <p>
              Upload your footage, pick your operations, and see a preview before you spend a credit.
            </p>
            {signedIn ? (
              <Link
                to="/studio"
                className="ft-cta ft-cta-primary"
                data-testid="features-cta-primary"
                onClick={handleCtaClick('features_final')}
              >
                Go to the Studio
              </Link>
            ) : (
              <button
                type="button"
                className="ft-cta ft-cta-primary"
                data-testid="features-cta-primary"
                onClick={handleSignInCta('features_final')}
              >
                Try Tweedia free
              </button>
            )}
          </section>
        </div>

      </main>
      <Footer />
    </div>
  );
}

export default FeaturesPage;
export { FEATURE_CARDS, MOTIONPACE_NAME, MOTIONTRIM_NAME, FIX_FLICKER };
